import React, { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, ChartLineUp, LockKey } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import SimulatorSection from "@/pages/SimulatorSection";
import { api } from "@/lib/api";
import { Highlight } from "@/components/Highlight";

const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_locacao-shield/artifacts/5k0zetdw_logo%20safimob%2003.JPG";
const BADGE_ICONS = [LockKey, ChartLineUp, ShieldCheck];

function Maintenance({ c }) {
  return (
    <div className="min-h-screen bg-[var(--bone)] flex items-center justify-center p-8" data-testid="maintenance-page">
      <div className="text-center max-w-md">
        <img src={LOGO} alt="Safimob" className="h-14 mx-auto mb-8" />
        <div className="eyebrow mb-3">{c.eyebrow}</div>
        <h1 className="font-display text-4xl tracking-tight text-slate-900">{c.title}</h1>
        <p className="text-slate-600 mt-4">{c.subtitle}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const goLogin = () => nav("/login");
  const [cfg, setCfg] = useState(null);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setCfg(r.data)).catch(() => setCfg({}));
    api.get("/content/public").then((r) => setContent(r.data)).catch(() => setContent(null));
  }, []);

  if (!cfg || !content) return <div className="min-h-screen bg-[var(--bone)]" />;
  if (cfg.maintenance_mode) return <Maintenance c={content.maintenance} />;
  const { nav: n, hero: h, plans: pl, footer: f } = content;

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="landing-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src={LOGO} alt="Safimob — Garantidora de Aluguel" className="h-11" />
          <div className="flex items-center gap-3">
            {cfg.show_simulator && <a href="#simulador" className="text-sm text-slate-600 hover:text-slate-900 hidden md:inline">{n.link_simular}</a>}
            {cfg.show_plans && <a href="#planos" className="text-sm text-slate-600 hover:text-slate-900 hidden md:inline">{n.link_planos}</a>}
            {cfg.show_login_button && <Button data-testid="nav-login-btn" onClick={goLogin} className="btn-primary">{n.btn_login}</Button>}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4" data-testid="hero-eyebrow">{h.eyebrow}</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-slate-900 max-w-3xl" data-testid="hero-title">
              <Highlight text={h.title} highlight={h.title_highlight} />
            </h1>
            <p className="mt-6 text-slate-600 max-w-xl text-lg leading-relaxed" data-testid="hero-subtitle">{h.subtitle}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {cfg.show_login_button && (
                <Button data-testid="hero-cta-login" onClick={goLogin} className="btn-primary h-12 px-6 text-base">
                  {h.cta_painel} <ArrowRight size={18} className="ml-2" />
                </Button>
              )}
              {cfg.show_simulator && (
                <Button data-testid="hero-cta-simular" variant={cfg.show_login_button ? "outline" : undefined} onClick={() => document.getElementById("simulador")?.scrollIntoView({ behavior: "smooth" })} className={`h-12 px-6 text-base ${cfg.show_login_button ? "" : "btn-primary"}`}>
                  {h.cta_simular} {!cfg.show_login_button && <ArrowRight size={18} className="ml-2" />}
                </Button>
              )}
            </div>
            {cfg.show_hero_badges && (
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500" data-testid="hero-badges">
                {(h.badges || []).map((b, i) => { const I = BADGE_ICONS[i % 3]; return <div key={b} className="flex items-center gap-2"><I size={16} /> {b}</div>; })}
              </div>
            )}
          </div>
          <div className="lg:col-span-5">
            <img src={h.image_url} alt="Edifício" className="rounded-lg border border-[var(--line)] w-full h-[520px] object-cover" />
          </div>
        </div>
      </section>

      {cfg.show_simulator && <SimulatorSection content={content.simulator} />}

      {cfg.show_plans && (
        <section id="planos" className="max-w-7xl mx-auto px-6 py-20">
          <div className="eyebrow mb-3">{pl.eyebrow}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl" data-testid="plans-title">{pl.title}</h2>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {pl.items.map((p, i) => (
              <div key={i} data-testid={`plan-${p.name.toLowerCase()}`} className={`card-flat p-8 relative ${p.highlight ? "border-[var(--emerald)] border-2" : ""}`}>
                {p.badge && <div className="eyebrow text-[var(--emerald)] mb-2">{p.badge}</div>}
                <div className="font-display text-3xl tracking-tight">{p.name}</div>
                <div className="text-slate-500 text-sm mt-1">Cobertura {p.mult} o aluguel</div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="metric-value text-5xl">{p.price}</span>
                  <span className="text-slate-500 text-sm">/ do aluguel</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-700 border border-slate-200">
                  Custo de saída: <strong>{p.exit}</strong>
                </div>
                <ul className="mt-6 space-y-2 text-sm text-slate-700">
                  {(p.features || []).map((ft) => (
                    <li key={ft} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--emerald)]" /> {ft}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-[var(--line)] mt-10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div data-testid="footer-left">{f.left}</div>
          <div>{f.right}</div>
        </div>
      </footer>
    </div>
  );
}
